import React, { useCallback, useEffect, useMemo, useState } from 'react';

import './AdvancedContactList.less';
import { translate } from '@portal/services/i18n';
import {
  ArrowRightOutlined,
  DeleteOutlined,
  PlusCircleFilled,
  StarFilled,
  StarOutlined,
} from '@ant-design/icons';
import Envelope from '@portal/assets/svg/contact-icons/ic_envelope.svg?react';
import { transfer } from '@portal/config/app.json';
import { Input } from 'antd';
import { FavoredContactsActions } from '@portal/actions';
import {
  maskCpfOrCnpj,
  removeAccent,
  unmaskField,
} from '@portal/services/masks';
import { useDispatch } from 'react-redux';
import useReduxState from '@portal/hooks/useReduxState';
import { useTheme } from '@material-ui/core';
import { TransferType } from '@portal/enum/transfers';
import MessageModal from '@portal/components/MessageModal/MessageModal';
import AdvancedAvatar from '../AdvancedAvatar/AdvancedAvatar';
import ThemedText from '@portal/components/ThemedText/ThemedText';

interface AdvancedContactListProps {
  type: TransferType;
  contacts: models.Contact[];
  onClickNewContact: () => void;
  onClickContact: (contact: models.Contact) => void;
  banks: models.Bank[];
  testId: string;
}

const AdvancedContactList = ({
  banks,
  type,
  contacts,
  onClickNewContact,
  onClickContact,
  testId,
}: AdvancedContactListProps) => {
  const dispatch = useDispatch();
  const { palette } = useTheme();

  const {
    paymentAccount: { selectedPaymentAccount },
    theme: { currentTheme },
  } = useReduxState();

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [favorites, setFavorites] = useState<models.Contact[]>([]);

  const hasContactByName = useCallback(
    (alias: string): boolean =>
      (search !== ''
        ? removeAccent(alias)
            .toLowerCase()
            .search(removeAccent(search).toLowerCase())
        : 0) !== -1,
    [search]
  );

  const hasContactByTaxId = useCallback(
    (taxId: string): boolean => {
      if (!/^\d+$/.test(search)) {
        return false;
      }

      return (
        (search !== '' ? unmaskField(taxId).search(unmaskField(search)) : 0) !==
        -1
      );
    },
    [search]
  );

  const byContactType = useCallback(
    (contact: models.Contact) => {
      if (type === TransferType.P2P) {
        return (
          contact.accountFavoredId &&
          (hasContactByName(contact.alias) ||
            hasContactByTaxId(contact.taxIdentifier))
        );
      }
      if (type === TransferType.TED && contact.bankCode) {
        return (
          contact.bankCode !== selectedPaymentAccount?.bankNumber &&
          (hasContactByName(contact.alias) ||
            hasContactByTaxId(contact.taxIdentifier ?? ''))
        );
      }
      if (type === TransferType.PIX && TransferType.PSP in contact) {
        if (contact.psp) {
          return (
            contact.bankAccount &&
            (hasContactByName(contact.alias) ||
              hasContactByTaxId(contact.taxIdentifier ?? ''))
          );
        }

        return contact.pixKey && hasContactByName(contact.alias);
      }
    },
    [hasContactByName, hasContactByTaxId, type, selectedPaymentAccount]
  );

  const sections = useMemo(
    () => [
      {
        title: translate('COMPONENTS.CONTACT_LIST.FAVORITES'),
        data: contacts
          .filter((contact) => contact.isFavorite)
          .filter(byContactType)
          .sort((a, b) => a.alias.localeCompare(b.alias)),
      },
      {
        title: translate('COMPONENTS.CONTACT_LIST.REGISTERED'),
        data: contacts
          .filter((contact) => !contact.isFavorite)
          .filter(byContactType)
          .filter((contact) => !favorites.includes(contact))
          .sort((a, b) => a.alias.localeCompare(b.alias)),
      },
    ],
    [contacts, favorites, byContactType]
  );

  useEffect(() => {
    setFavorites(contacts.filter((contact) => contact.isFavorite));
  }, [contacts]);

  const handleFavorite = (id: string | null) => {
    if (!id) {
      return;
    }

    const item = contacts.find((contact) => contact.id === id);

    if (!item) {
      return;
    }

    if (favorites.some((favorite) => favorite.id === id)) {
      setFavorites((oldFavorites) =>
        oldFavorites.filter((favorite) => favorite.id !== id)
      );
      dispatch(FavoredContactsActions.favoriteFavoredContact(false, id));
    } else {
      setFavorites((oldFavorites) => [item, ...oldFavorites]);
      dispatch(FavoredContactsActions.favoriteFavoredContact(true, id));
    }
  };

  const getPlaceholder = () => {
    switch (type) {
      case TransferType.PIX:
        return 'PIX';

      case TransferType.TED:
      case TransferType.P2P:
        return transfer.personalAccount && !transfer.p2p
          ? 'TRANSFER_INVESTMENT'
          : 'TRANSFER';

      default:
        return 'TRANSFER';
    }
  };

  const handleDeleteContact = () => {
    if (selectedId) {
      dispatch(FavoredContactsActions.deleteFavoredContact(selectedId));
    }
    setIsDeleteModalVisible(false);
  };

  const getBankImgUrl = (contact: models.Contact) => {
    if (contact.bankCode && banks) {
      const favoredBank = banks.find((bank) => bank.code === contact.bankCode);

      return favoredBank?.iconUrl || undefined;
    }

    return undefined;
  };

  return (
    <>
      <div className="advanced-contact-list" data-testid={testId}>
        <div className="advanced-contact-list__row">
          <PlusCircleFilled />
          <ThemedText variant="body">
            {translate('COMPONENTS.CONTACT_LIST.NEW_CONTACT.TITLE')}
          </ThemedText>
        </div>
        <div
          className="advanced-contact-list__add-contact"
          style={{ background: currentTheme.background }}
          onClick={onClickNewContact}
        >
          <div className="advanced-contact-list__add-contact__info">
            <div className="advanced-contact-list__add-contact__info__icon">
              <Envelope />
            </div>
            <div className="advanced-contact-list__add-contact__info__text">
              <ThemedText variant="h3">
                {translate('COMPONENTS.CONTACT_LIST.NEW_CONTACT.TITLE')}
              </ThemedText>
              <ThemedText variant="body">
                {translate('COMPONENTS.CONTACT_LIST.NEW_CONTACT.SUBTITLE')}
              </ThemedText>
            </div>
          </div>
          <div className="advanced-contact-list__add-contact__info__icon transparent-button">
            <ArrowRightOutlined />
          </div>
        </div>
        {sections.every((section) => section.data.length === 0) && (
          <ThemedText className="contact-list__empty" variant="body">
            {translate('COMPONENTS.CONTACT_LIST.EMPTY')}
          </ThemedText>
        )}
      </div>
      <div className="advanced-contact-list">
        <div className="contact-list__header">
          <Input
            className="contact-list__header__input"
            placeholder={translate(
              `COMPONENTS.CONTACT_LIST.PLACEHOLDER.${getPlaceholder()}`
            )}
            onChange={({ target: { value } }) => setSearch(value)}
          />
        </div>
        {sections.map((section) => (
          <div key={section.title}>
            {section.data.length > 0 && (
              <ThemedText
                variant="h1"
                className="contact-list__container__contact-list-title"
              >
                {section.title}
              </ThemedText>
            )}
            {section.data.map((contact) => (
              <>
                <div
                  key={contact.id}
                  className="advanced-contact-list__contacts"
                >
                  <div
                    className="advanced-contact-list__contacts__info"
                    onClick={() => onClickContact(contact)}
                  >
                    <div className="advanced-contact-list__contacts__info__avatar">
                      <AdvancedAvatar
                        name={contact.alias}
                        image={getBankImgUrl(contact)}
                      />
                    </div>
                    <div className="advanced-contact-list__contacts__item__info__details">
                      <ThemedText variant="h6">{contact.alias}</ThemedText>
                      {(type === TransferType.TED ||
                        type === TransferType.PIX) &&
                      contact.bankAccount ? (
                        <ThemedText
                          variant="body"
                          className="contact-list-item__contacts__list__bank-data__second-line"
                        >
                          {contact.bankUserName} &bull;{' '}
                          {translate(
                            'PAGES.HOME.TRANSFER.FAVORED.BRANCH_ACCOUNT'
                          )}
                          : {contact.bankAgency} &bull;{' '}
                          {translate(
                            'PAGES.HOME.TRANSFER.FAVORED.BANK_ACCOUNT'
                          )}
                          : {contact.bankAccount}-{contact.bankAccountDigit}
                        </ThemedText>
                      ) : null}
                      {type === TransferType.P2P && contact.accountFavoredId ? (
                        <ThemedText
                          variant="body"
                          className="contact-list-item__contacts__list__bank-data__second-line"
                        >
                          {maskCpfOrCnpj(contact.taxIdentifier)}
                        </ThemedText>
                      ) : null}
                      {type === TransferType.PIX && contact.pixKey ? (
                        <ThemedText
                          variant="body"
                          className="contact-list-item__contacts__list__bank-data__second-line"
                        >
                          {translate('COMPONENTS.CONFIRMATION_DETAILS.KEY')}:{' '}
                          {contact.pixKey}
                        </ThemedText>
                      ) : null}
                    </div>
                  </div>
                  <div className="advanced-contact-list__contacts__actions">
                    <button
                      className="advanced-contact-list__contacts__actions__star"
                      onClick={() => handleFavorite(contact.id ?? null)}
                    >
                      {contact.isFavorite ? <StarFilled /> : <StarOutlined />}
                    </button>
                    <button
                      className="advanced-contact-list__contacts__actions__delete"
                      onClick={() => {
                        setIsDeleteModalVisible(true);
                        setSelectedId(contact.id ?? null);
                      }}
                    >
                      <DeleteOutlined />
                    </button>
                  </div>
                </div>
              </>
            ))}
          </div>
        ))}
      </div>
      <MessageModal
        open={isDeleteModalVisible}
        onConfirm={handleDeleteContact}
        onCancel={() => setIsDeleteModalVisible(false)}
        setVisible={setIsDeleteModalVisible}
        buttonOkText={translate('COMPONENTS.CONTACT_LIST.DELETE_MODAL.DELETE')}
        buttonCancelText={translate(
          'COMPONENTS.CONTACT_LIST.DELETE_MODAL.CANCEL'
        )}
        title={translate('COMPONENTS.CONTACT_LIST.DELETE_MODAL.TITLE')}
        message={translate('COMPONENTS.CONTACT_LIST.DELETE_MODAL.SUBTITLE')}
        confirmButtonCustomStyles={{ background: palette.error.main }}
      />
    </>
  );
};

export default AdvancedContactList;
